import { AppointmentStatus } from '../appointment/appointment';
import { DepositStatus } from '../deposit/deposit-metadata';

export enum StatusIndicatorMode {
  Character = 'character',
  Icon = 'icon',
}

export enum LabJobBadgeState {
  Received = 'received',
  SentOrSending = 'sentOrSending',
  Overdue = 'overdue',
}

export enum AppointmentCombinedIndicator {
  NotConfirmed = 'notConfirmed', // handles - AppointmentStatus.Unscheduled, AppointmentStatus.Scheduled,
  HasArrived = 'hasArrived', // handles - AppointmentStatus.Arrived, AppointmentStatus.CheckedIn
}

export enum BookingIndicator {
  OnlineBooking = 'onlineBooking',
  BookedExternally = 'bookedExternally',
}

export enum DepositIndicator {
  Paid = 'depositPaid',
  Authorised = 'depositAuthorised',
  Required = 'depositRequired',
  Pending = 'depositPending',
  Forfeited = 'depositForfeited',
}

export enum StatusIndicatorType {
  // Appointment
  AppointmentNotConfirmed = AppointmentCombinedIndicator.NotConfirmed,
  AppointmentConfirmed = AppointmentStatus.Confirmed,
  AppointmentHasArrived = AppointmentCombinedIndicator.HasArrived,
  AppointmentInProgress = AppointmentStatus.InProgress,
  AppointmentCheckingOut = AppointmentStatus.CheckingOut,
  AppointmentComplete = AppointmentStatus.Complete,
  AppointmentCancelled = AppointmentStatus.Cancelled,
  // Lab job
  LabJobOverdue = LabJobBadgeState.Overdue,
  LabJobSentOrSending = LabJobBadgeState.SentOrSending,
  LabJobReceived = LabJobBadgeState.Received,
  // Booking
  OnlineBooking = BookingIndicator.OnlineBooking,
  BookedExternally = BookingIndicator.BookedExternally,
  DepositPaid = DepositIndicator.Paid,
  DepositAuthorised = DepositIndicator.Authorised,
  DepositRequired = DepositIndicator.Required,
  DepositPending = DepositIndicator.Pending,
  DepositForfeited = DepositIndicator.Forfeited,
}

export interface IStatusIndicatorConfig {
  mode: StatusIndicatorMode;
  backgroundColour: string;
  enabled: boolean;
  colour?: string;
  character?: string;
  icon?: string;
}

export const STATUS_INDICATOR_TYPES = Object.values(
  StatusIndicatorType
) as StatusIndicatorType[];

/**
 * `Refunded`, `Waived`, `Cancelled` and `Released` map to `undefined` on
 * purpose - nothing is held or owed, so they warrant no at-a-glance badge. They
 * are listed explicitly rather than omitted so the map stays exhaustive and no
 * status can resolve to a generic fallback badge.
 */
const DEPOSIT_STATUS_INDICATORS: Record<
  DepositStatus,
  StatusIndicatorType | undefined
> = {
  [DepositStatus.Required]: StatusIndicatorType.DepositRequired,
  [DepositStatus.Pending]: StatusIndicatorType.DepositPending,
  [DepositStatus.Authorised]: StatusIndicatorType.DepositAuthorised,
  [DepositStatus.Paid]: StatusIndicatorType.DepositPaid,
  [DepositStatus.Forfeited]: StatusIndicatorType.DepositForfeited,
  [DepositStatus.Refunded]: undefined,
  [DepositStatus.Waived]: undefined,
  [DepositStatus.Cancelled]: undefined,
  [DepositStatus.Released]: undefined,
};

export function getStatusIndicatorTypeFromDepositStatus(
  status: DepositStatus
): StatusIndicatorType | undefined {
  return DEPOSIT_STATUS_INDICATORS[status];
}

export function getStatusIndicatorTypeFromAppointmentStatus(
  status: AppointmentStatus
): StatusIndicatorType | undefined {
  const entry = Object.keys(DEFAULT_STATUS_INDICATOR_CONFIG).find(
    (key) => key === status
  );
  return entry ? (entry as StatusIndicatorType) : undefined;
}

/**
 * Every deposit state shares one glyph on purpose: a deposit reads as a deposit
 * at a glance, and the badge's background colour is what separates paid from
 * required from forfeited. Give any single state its own icon and the set stops
 * being scannable as one family.
 */
export const DEPOSIT_INDICATOR_ICON = 'attach_money';

export const DEFAULT_STATUS_INDICATOR_CONFIG: {
  [key in StatusIndicatorType]: IStatusIndicatorConfig;
} = {
  [StatusIndicatorType.AppointmentNotConfirmed]: {
    mode: StatusIndicatorMode.Character,
    character: 'C',
    colour: '#ffffff',
    backgroundColour: '#ef4444',
    enabled: true,
  },
  [StatusIndicatorType.AppointmentConfirmed]: {
    mode: StatusIndicatorMode.Character,
    character: 'C',
    colour: '#ffffff',
    backgroundColour: '#f59e0b',
    enabled: false,
  },
  [StatusIndicatorType.AppointmentHasArrived]: {
    mode: StatusIndicatorMode.Character,
    character: 'A',
    colour: '#ffffff',
    backgroundColour: '#10b981',
    enabled: true,
  },
  [StatusIndicatorType.AppointmentInProgress]: {
    mode: StatusIndicatorMode.Character,
    character: 'P',
    colour: '#ffffff',
    backgroundColour: '#6366f1',
    enabled: false,
  },
  [StatusIndicatorType.AppointmentCheckingOut]: {
    mode: StatusIndicatorMode.Character,
    character: 'O',
    colour: '#ffffff',
    backgroundColour: '#8b5cf6',
    enabled: false,
  },
  [StatusIndicatorType.AppointmentComplete]: {
    mode: StatusIndicatorMode.Icon,
    icon: 'check_circle',
    colour: '#ffffff',
    backgroundColour: '#10b981',
    enabled: false,
  },
  [StatusIndicatorType.AppointmentCancelled]: {
    mode: StatusIndicatorMode.Icon,
    icon: 'cancel',
    colour: '#ffffff',
    backgroundColour: '#ef4444',
    enabled: false,
  },
  [StatusIndicatorType.LabJobOverdue]: {
    mode: StatusIndicatorMode.Character,
    character: 'L',
    colour: '#ffffff',
    backgroundColour: '#ef4444',
    enabled: true,
  },
  [StatusIndicatorType.LabJobSentOrSending]: {
    mode: StatusIndicatorMode.Character,
    character: 'L',
    colour: '#ffffff',
    backgroundColour: '#f59e0b',
    enabled: true,
  },
  [StatusIndicatorType.LabJobReceived]: {
    mode: StatusIndicatorMode.Character,
    character: 'L',
    colour: '#ffffff',
    backgroundColour: '#10b981',
    enabled: true,
  },
  [StatusIndicatorType.OnlineBooking]: {
    mode: StatusIndicatorMode.Icon,
    icon: 'public',
    colour: '#ffffff',
    backgroundColour: '#6366f1',
    enabled: true,
  },
  [StatusIndicatorType.BookedExternally]: {
    mode: StatusIndicatorMode.Icon,
    icon: 'webhook',
    colour: '#ffffff',
    backgroundColour: '#8b5cf6',
    enabled: true,
  },
  [StatusIndicatorType.DepositPaid]: {
    mode: StatusIndicatorMode.Icon,
    icon: DEPOSIT_INDICATOR_ICON,
    colour: '#ffffff',
    backgroundColour: '#047857',
    enabled: true,
  },
  [StatusIndicatorType.DepositAuthorised]: {
    mode: StatusIndicatorMode.Icon,
    icon: DEPOSIT_INDICATOR_ICON,
    colour: '#ffffff',
    backgroundColour: '#d97706',
    enabled: true,
  },
  [StatusIndicatorType.DepositRequired]: {
    mode: StatusIndicatorMode.Icon,
    icon: DEPOSIT_INDICATOR_ICON,
    colour: '#ffffff',
    backgroundColour: '#ea580c',
    enabled: true,
  },
  [StatusIndicatorType.DepositPending]: {
    mode: StatusIndicatorMode.Icon,
    icon: DEPOSIT_INDICATOR_ICON,
    colour: '#ffffff',
    backgroundColour: '#2563eb',
    enabled: true,
  },
  [StatusIndicatorType.DepositForfeited]: {
    mode: StatusIndicatorMode.Icon,
    icon: DEPOSIT_INDICATOR_ICON,
    colour: '#ffffff',
    backgroundColour: '#dc2626',
    enabled: true,
  },
};

export const DEFAULT_MATERIAL_ICONS = [
  //  Mandatory Status Indicators
  'check_circle',
  'cancel',
  'webhook',
  'public',

  //  System Status & Alerts
  'pending',
  'hourglass_empty',
  'warning',
  'error_outline',
  'info',
  'help_outline',
  'sync',
  'update',
  'verified',
  'lock',
  'visibility',

  //  Patient Management & Scheduling
  'calendar_today',
  'schedule',
  'event_available',
  'event_busy',
  'person',
  'people',
  'person_add',
  'badge',
  'contact_phone',
  'contact_mail',
  'history',
  'face',
  'child_care',
  'accessibility_new',
  'notifications',
  'alarm',
  'edit_calendar',

  //  Clinical & Treatment
  'medical_services',
  'healing',
  'clean_hands',
  'coronavirus',
  'masks',
  'content_cut',
  'colorize',
  'medication',
  'bloodtype',
  'biotech',
  'science',
  'health_and_safety',
  'spa',
  'favorite',
  'monitor_heart',
  'straighten',
  'view_day',
  'opacity',

  //  Records, Imaging & Notes
  'description',
  'note_add',
  'edit_note',
  'folder_shared',
  'image',
  'camera_alt',
  'grid_on',
  'assignment',
  'assignment_turned_in',
  'picture_as_pdf',
  'print',
  'save',
  'upload_file',
  'attach_file',
  'mic',
  'folder_special',
  'filter_center_focus',
  'photo_library',

  //  Billing, Insurance & Finance
  'receipt_long',
  'payments',
  'attach_money',
  'credit_card',
  'account_balance_wallet',
  'request_quote',
  'shopping_cart',
  'calculate',
  'analytics',
  'trending_up',
  'paid',
  'price_check',
  'savings',
  'monetization_on',
  'work_history',

  //  Tools, Equipment & Inventory
  'build',
  'home_repair_service',
  'inventory_2',
  'local_shipping',
  'archive',
  'unarchive',
  'qr_code_scanner',
  'chair',
  'lightbulb',
  'water_drop',
  'air',
  'construction',
  'delete',
  'auto_delete',

  //  Staff & Communication
  'groups',
  'support_agent',
  'chat',
  'email',
  'call',
  'video_call',
  'speaker_notes',
  'announcement',
  'supervisor_account',
  'school',
  'thumb_up',
  'star',
  'share',

  //  App Navigation & Settings
  'dashboard',
  'settings',
  'menu',
  'close',
  'arrow_forward',
  'arrow_back',
  'more_vert',
  'logout',
  'login',
  'home',
];
